import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
    Heart, Star, Baby, Dog, Cat, Brain, Sparkles, ChevronRight, AlertCircle
} from 'lucide-react';

const TemperamentoCard = ({ temperamento }) => {
    if (!temperamento) return null;
    const { personalidad, sociabilidad } = temperamento;

    const SectionHeader = ({ icon: Icon, title, description }) => (
        <div className="space-y-3">
            {/* Icon and Title wrapper */}
            <div className="flex items-center gap-3">
                <Icon
                    className="h-8 w-8 text-rose-500 flex-shrink-0"
                    strokeWidth={2}
                />
                <h2 className="text-[1.35rem] font-semibold text-gray-900 leading-tight">
                    {title}
                </h2>
            </div>

            {/* Description with proper spacing and color */}
            {description && (
                <p className="text-[0.95rem] text-gray-600 leading-relaxed ml-[2.75rem]">
                    {description}
                </p>
            )}
        </div>
    );

    const CompatibilityBadge = ({ nivel }) => {
        const getColorsByLevel = (nivel) => {
            if (nivel.includes('Excelente')) return 'from-emerald-500 to-emerald-600 text-emerald-50';
            if (nivel.includes('Buena')) return 'from-blue-500 to-blue-600 text-blue-50';
            return 'from-amber-500 to-amber-600 text-amber-50';
        };

        return (
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-br ${getColorsByLevel(nivel)} rounded-full shadow-md`}>
                <Star className="h-4 w-4" strokeWidth={2} />
                <span className="font-semibold text-base">{nivel}</span>
            </div>
        );
    };

    const InfoBox = ({ icon: Icon, title, content, color = "blue", className = "" }) => (
        <div className={`bg-white rounded-2xl border border-${color}-100 p-5 hover:shadow-xl transition-all duration-300 ${className}`}>
            <div className="flex items-center gap-3 mb-4">
                <Icon className={`h-6 w-6 text-${color}-500`} strokeWidth={1.5} />
                <h4 className={`text-lg font-semibold text-${color}-700`}>{title}</h4>
            </div>
            {content}
        </div>
    );

    const ListItem = ({ icon: Icon, text, color = "blue", isHighlighted = false }) => (
        <div className={`flex items-start gap-3 ${isHighlighted ? `bg-${color}-50` : ''} p-3 rounded-xl transition-colors duration-200 hover:bg-${color}-50/80`}>
            <Icon className={`h-5 w-5 mt-0.5 text-${color}-500 flex-shrink-0`} strokeWidth={1.5} />
            <p className="text-gray-700 text-base leading-relaxed">{text}</p>
        </div>
    );

    return (
        <Card className="w-full max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-white shadow-xl">
            <CardContent className="p-4 sm:p-6 md:p-8">
                <SectionHeader
                    icon={Heart}
                    title="Temperamento y Sociabilidad"
                    description="Descubre la personalidad única de tu compañero y cómo se relaciona con otros"
                    className="mb-6 sm:mb-8"
                />

                <div className="space-y-6 sm:space-y-8">
                    {/* Personality Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                        <div className="space-y-4 sm:space-y-6">
                            <h3 className="text-xl sm:text-2xl md:text-[1.75rem] font-bold text-gray-800 flex items-center gap-2">
                                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-500" strokeWidth={1.5} />
                                <span className="relative top-[1px]">Rasgos Positivos</span>
                            </h3>
                            <div className="space-y-2.5 sm:space-y-3">
                                {personalidad.rasgosPositivos.map((rasgo, idx) => (
                                    <ListItem
                                        key={idx}
                                        icon={Star}
                                        text={rasgo}
                                        color="emerald"
                                        isHighlighted={idx % 2 === 0}
                                        className="text-sm sm:text-base"
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4 sm:space-y-6">
                            <h3 className="text-xl sm:text-2xl md:text-[1.75rem] font-bold text-gray-800 flex items-center gap-2">
                                <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" strokeWidth={1.5} />
                                <span className="relative top-[1px]">Aspectos de Educación</span>
                            </h3>
                            <div className="space-y-2.5 sm:space-y-3">
                                {personalidad.aspectosEducacion.map((aspecto, idx) => (
                                    <ListItem
                                        key={idx}
                                        icon={AlertCircle}
                                        text={aspecto}
                                        color="blue"
                                        isHighlighted={idx % 2 === 0}
                                        className="text-sm sm:text-base"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sociability Section */}
                    <div className="pt-2">
                        <h3 className="text-xl sm:text-2xl md:text-[1.75rem] font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2">
                            <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-rose-500" strokeWidth={1.5} />
                            <span className="relative top-[1px]">Relaciones Sociales</span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            <InfoBox
                                icon={Baby}
                                title="Con Niños"
                                color="blue"
                                className="text-sm sm:text-base"
                                content={
                                    <div className="space-y-3 sm:space-y-4">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <CompatibilityBadge
                                                nivel={sociabilidad.conNinos.compatibilidad}
                                                className="text-xs sm:text-sm"
                                            />
                                        </div>
                                        <div className="space-y-2 sm:space-y-2.5">
                                            {sociabilidad.conNinos.recomendaciones.map((rec, idx) => (
                                                <ListItem
                                                    key={idx}
                                                    icon={ChevronRight}
                                                    text={rec}
                                                    color="blue"
                                                    isHighlighted={idx % 2 === 0}
                                                    className="text-sm sm:text-base"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                }
                            />
                            <InfoBox
                                icon={Dog}
                                title="Con Otros Perros"
                                color="purple"
                                className="text-sm sm:text-base"
                                content={
                                    <div className="space-y-3 sm:space-y-4">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <CompatibilityBadge
                                                nivel={sociabilidad.conOtrosPerros.compatibilidad}
                                                className="text-xs sm:text-sm"
                                            />
                                        </div>
                                        <div className="space-y-2 sm:space-y-2.5">
                                            {sociabilidad.conOtrosPerros.consejos.map((consejo, idx) => (
                                                <ListItem
                                                    key={idx}
                                                    icon={ChevronRight}
                                                    text={consejo}
                                                    color="purple"
                                                    isHighlighted={idx % 2 === 0}
                                                    className="text-sm sm:text-base"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                }
                            />
                            <InfoBox
                                icon={Cat}
                                title="Con Gatos"
                                color="rose"
                                className="text-sm sm:text-base"
                                content={
                                    <div className="space-y-3 sm:space-y-4">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <CompatibilityBadge
                                                nivel={sociabilidad.conGatos.compatibilidad}
                                                className="text-xs sm:text-sm"
                                            />
                                        </div>
                                        <div className="space-y-2 sm:space-y-2.5">
                                            {sociabilidad.conGatos.tips.map((tip, idx) => (
                                                <ListItem
                                                    key={idx}
                                                    icon={ChevronRight}
                                                    text={tip}
                                                    color="rose"
                                                    isHighlighted={idx % 2 === 0}
                                                    className="text-sm sm:text-base"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default TemperamentoCard;